import React, { useState } from 'react';
import { Switch, Tooltip, Popover, Space, Typography, App } from 'antd';
import { AimOutlined } from '@ant-design/icons';

import { imagenService, fixUrl } from '../services/api';

const { Text } = Typography;

// LAS NUEVE POSICIONES de un `background-position`, como un teclado numérico.
//
// Nueve y no un punto libre a propósito: el problema que esto resuelve es "la
// tarjeta de OP-2 me cae sobre la cabeza del toro", y para eso alcanza con
// decir de qué lado está el animal. Un selector de coordenadas finas pide
// precisión que nadie necesita y obliga a explicar qué son x e y.
const ANCLAS = [
  { x: 0, y: 0, titulo: 'Arriba izquierda' },
  { x: 50, y: 0, titulo: 'Arriba' },
  { x: 100, y: 0, titulo: 'Arriba derecha' },
  { x: 0, y: 50, titulo: 'Izquierda' },
  { x: 50, y: 50, titulo: 'Centro' },
  { x: 100, y: 50, titulo: 'Derecha' },
  { x: 0, y: 100, titulo: 'Abajo izquierda' },
  { x: 50, y: 100, titulo: 'Abajo' },
  { x: 100, y: 100, titulo: 'Abajo derecha' },
];

/**
 * El encuadre de UNA foto: dónde queda anclado el recorte.
 *
 * SE VE EL EFECTO MIENTRAS SE ELIGE. La previsualización usa la misma foto con
 * el mismo `background-position` y una proporción parecida a la del bloque de
 * OP-2, así que el productor no tiene que ir al micrositio a ver si acertó.
 */
const SelectorEncuadre = ({ imagen, onCambio }) => {
  // `App.useApp()` y no el `message` estatico de antd: el estatico no toma el
  // tema ni el contexto del <App>, asi que el aviso sale con otro aspecto que
  // el resto de la pantalla.
  const { message } = App.useApp();
  const [foco, setFoco] = useState({ x: imagen.foco_x ?? 50, y: imagen.foco_y ?? 50 });
  const [guardando, setGuardando] = useState(false);

  const elegir = async (ancla) => {
    const previo = foco;
    setFoco({ x: ancla.x, y: ancla.y });
    setGuardando(true);
    try {
      const res = await imagenService.actualizarPresentacion(imagen.id_imagen, {
        foco_x: ancla.x, foco_y: ancla.y,
      });
      onCambio(res.data);
    } catch (err) {
      // SE VUELVE ATRÁS EN PANTALLA. Dejar el botón marcado cuando el guardado
      // falló hace creer que quedó guardado, y el productor se entera recién
      // cuando mira el micrositio.
      setFoco(previo);
      message.error(err.response?.data?.error || 'No pudimos guardar el encuadre');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ width: 260 }}>
      <div
        style={{
          width: '100%',
          // 21:9, parecida a la franja que recorta OP-2. No es la medida exacta
          // —esa depende de la pantalla— pero muestra el problema real: qué se
          // pierde arriba y abajo cuando la foto se corta apaisada.
          aspectRatio: '21 / 9',
          backgroundImage: `url("${encodeURI(fixUrl(imagen.url_thumb || imagen.url_imagen))}")`,
          backgroundSize: 'cover',
          backgroundPosition: `${foco.x}% ${foco.y}%`,
          borderRadius: 6,
          marginBottom: 10,
        }}
      />
      <div
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4,
          opacity: guardando ? 0.6 : 1,
        }}
      >
        {ANCLAS.map((a) => {
          const activa = foco.x === a.x && foco.y === a.y;
          return (
            <Tooltip key={`${a.x}-${a.y}`} title={a.titulo}>
              <button
                type="button"
                onClick={() => elegir(a)}
                disabled={guardando}
                aria-label={a.titulo}
                aria-pressed={activa}
                style={{
                  height: 26, cursor: guardando ? 'default' : 'pointer',
                  border: `1px solid ${activa ? '#b6672f' : '#e4e1db'}`,
                  background: activa ? '#b6672f' : '#fff',
                  borderRadius: 4, padding: 0,
                }}
              />
            </Tooltip>
          );
        })}
      </div>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
        Dónde queda anclada la foto cuando se recorta. Sirve para la plantilla
        OP-2 del catálogo, que la usa de fondo a ancho completo.
      </Text>
    </div>
  );
};

/**
 * Los tres controles de presentación de una foto.
 *
 * VIVE EN `components/` y no dentro de una página porque lo usan DOS dominios:
 * la tabla de Animales → Fotos y videos, y el modal del catálogo de genética en
 * Herramientas. Es la misma regla que AGENTS.md fija para los hooks —"si empieza
 * a usarse desde otro dominio, se muda, no se copia"— y la que ya movió a
 * `LineChart` acá. Ir a buscarlo adentro de la página de otro dominio no es
 * copiarlo, pero tampoco es tenerlo donde va.
 *
 * Los tres cambian cómo se USA una imagen que ya existe, y ninguno toca el
 * archivo: por eso van juntos en una sola columna y guardan solos, sin un botón
 * de "guardar" aparte.
 */
const PresentacionImagen = ({ imagen, onCambio }) => {
  const { message } = App.useApp();
  const [guardando, setGuardando] = useState(null);

  const cambiar = async (campo, valor) => {
    setGuardando(campo);
    try {
      const res = await imagenService.actualizarPresentacion(imagen.id_imagen, {
        [campo]: valor ? 1 : 0,
      });
      onCambio(res.data);
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos guardar el cambio');
    } finally {
      setGuardando(null);
    }
  };

  return (
    <Space size={12} wrap>
      <Tooltip title="La foto que abre el catálogo. Sólo puede haber una por animal: al marcar ésta se desmarca la anterior.">
        <span>
          <Switch
            size="small"
            checked={Boolean(imagen.es_portada)}
            loading={guardando === 'es_portada'}
            onChange={(v) => cambiar('es_portada', v)}
          />
          <Text style={{ fontSize: 12, marginLeft: 6 }}>Portada</Text>
        </span>
      </Tooltip>

      <Tooltip title="Si se muestra en la tira de fotos de la ficha. Una foto preparada para el fondo de OP-2 puede quedar fuera del carrusel sin dejar de ser la portada.">
        <span>
          <Switch
            size="small"
            checked={Boolean(imagen.en_carrusel)}
            loading={guardando === 'en_carrusel'}
            onChange={(v) => cambiar('en_carrusel', v)}
          />
          <Text style={{ fontSize: 12, marginLeft: 6 }}>Carrusel</Text>
        </span>
      </Tooltip>

      <Popover
        trigger="click"
        placement="left"
        content={<SelectorEncuadre imagen={imagen} onCambio={onCambio} />}
        title="Encuadre"
      >
        <Tooltip title="Dónde queda anclada la foto cuando se recorta">
          <AimOutlined style={{ cursor: 'pointer', fontSize: 16 }} />
        </Tooltip>
      </Popover>
    </Space>
  );
};

export default PresentacionImagen;
